import React from 'react';
import PropTypes from 'prop-types';
import { compose, isHigherAdmin } from '../../../../services/utilityFunctions';
import translate from '../../../../commonComponents/translate';
import withLoggedUser from '../../../../commonComponents/withLoggedUser';
import Form from '../../../../commonComponents/forms/Form';
import FormInput from '../../../../commonComponents/forms/FormInput';
import Separator from '../../../../commonComponents/forms/Separator';
import UserProp from '../../../profile/propTypes/userProp';
import CountryProp from '../../../../commonComponents/forms/Address/CountryProp';
import FormAddressFragment from '../../../../commonComponents/forms/Address/FormAddressFragment';
import TaxIdTypeProp from '../../propTypes/TaxIdTypeProp';
import BillingProp from '../../../profile/propTypes/billingProp';
import BusinessTypeProp from '../../../profile/propTypes/businessTypeProp';

class Billing extends React.Component {
  constructor(props) {
    super(props);
    const { billing, fieldsRequired } = props;
    const {
      billingAddress,
      invoiceEmail,
      businessType,
      taxId,
      taxCode,
      taxType,
      billingLanguage,
    } = billing;
    this.state = {
      firstName: billingAddress?.firstName ?? '',
      lastName: billingAddress?.lastName ?? '',
      companyName: billingAddress?.companyName ?? '',
      phone: billingAddress?.phone ?? '',
      email: invoiceEmail,
      billingLanguage,
      tax_info: {
        businessType,
        taxId,
        taxCode,
        taxType,
      },
      address: {
        address: billingAddress?.address ?? '',
        address_1: billingAddress?.address_1 ?? '',
        address_2: billingAddress?.address_2 ?? '',
        city: billingAddress?.city ?? '',
        zipCode: billingAddress?.zipCode ?? '',
        state: billingAddress?.state ?? '',
        country: billingAddress?.country ?? '',
        countryCode: billingAddress?.countryCode ?? '',
        provinceCode: billingAddress?.provinceCode ?? '',
        provinceCountryCode: billingAddress?.provinceCountryCode ?? '',
      },
    };

    this.validationRules = {
      firstName: { required: false },
      lastName: { required: false },
      companyName: { required: fieldsRequired },
      phone: { required: false },
      email: { required: fieldsRequired },
      billingLanguage: { required: fieldsRequired },
      businessType: { required: fieldsRequired },
      taxId: { required: false },
      taxCode: { required: fieldsRequired },
      address_1: { required: false },
      address_2: { required: false },
      city: { required: false },
      zipCode: { required: false },
      state: { required: false },
      countryCode: { required: fieldsRequired },
    };
    this.nextStep = 6;
    this.submitButton = null;

    this.createInput = this.createInput.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleAddressChange = this.handleAddressChange.bind(this);
    this.handleSubSectionChange = this.handleSubSectionChange.bind(this);
    this.handleNavigation = this.handleNavigation.bind(this);
    this.validateAndLeave = this.validateAndLeave.bind(this);
  }

  componentDidMount() {
    const { showValidationErrors } = this.props;
    if (showValidationErrors) {
      if (this.submitButton) {
        this.submitButton.click();
      }
    }
  }

  handleNavigation(validated) {
    return () => {
      if (!validated) {
        const el = document.querySelector('.has-danger');
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'start',
          });
        }
        this.props.resetShowErrors();
      } else {
        const {
          firstName,
          lastName,
          companyName,
          phone,
          email,
          tax_info,
          address,
          billingLanguage,
        } = this.state;
        const { businessType, taxId, taxCode, taxType } = tax_info;
        this.props.onNavigate(
          {
            billing: {
              billingAddress: {
                ...address,
                firstName,
                lastName,
                companyName,
                phone,
                email,
              },
              billingLanguage,
              businessType,
              invoiceEmail: email,
              taxId,
              taxCode,
              taxType,
            },
          },
          this.nextStep,
          validated,
        );
      }
    };
  }

  handleChange(field) {
    return value => {
      this.setState({ [field]: value });
    };
  }

  handleAddressChange(newAddress) {
    this.setState(({ address }) => ({
      address: { ...address, ...newAddress },
    }));
  }

  handleSubSectionChange(section) {
    return field => value => {
      this.setState(
        state => {
          if (field === 'taxCode') {
            let { taxType } = state[section];
            const taxIdType = this.props.taxIdTypes.find(
              txId => txId.taxCode === value,
            );
            if (taxIdType) {
              ({ taxType } = taxIdType);
            }
            return {
              ...state,
              [section]: { ...state[section], [field]: value, taxType },
            };
          }
          return {
            ...state,
            [section]: { ...state[section], [field]: value },
          };
        },
        () => {
          if (field === 'country') {
            this.props.onCountryChange(value);
          }
        },
      );
    };
  }

  validateAndLeave(nextStep) {
    this.nextStep = nextStep;
    if (this.submitButton) this.submitButton.click();
  }

  createInput(section, field, disabled, typeParam) {
    const { t, fieldsRequired, taxIdTypes, businessTypes, languages } =
      this.props;

    const type = typeParam || 'text';

    let value;
    let changeHandler;
    if (section === 'main') {
      value = this.state[field];
      changeHandler = this.handleChange(field);
    } else {
      value = this.state[section][field];
      changeHandler = this.handleSubSectionChange(section)(field);
    }
    let placeholder = '';
    let inputProps = {};
    if (section === 'main') {
      if (field === 'billingLanguage') {
        inputProps = {
          required: fieldsRequired,
          items: languages.map(l => ({ id: l.code, text: l.name })),
        };
      }
    }
    if (section === 'tax_info') {
      if (field === 'businessType') {
        inputProps = {
          required: fieldsRequired,
          items: [
            { id: 'individual', text: t('billing_business_type_individual') },
            ...businessTypes.map(b => ({
              id: b.code,
              text: b.emoji ? `${b.emoji} ${b.name}` : b.name,
            })),
          ],
        };
      }
      if (field === 'taxCode') {
        inputProps = {
          required: fieldsRequired,
          items: taxIdTypes.map(c => ({
            id: c.taxCode,
            text:
              c.taxType === 'eu_vat'
                ? `${c.flagEmoji} ${c.countryName} VAT number`
                : `${c.flagEmoji} ${c.name}`,
          })),
        };
      }
      if (field === 'taxId') {
        const { taxCode } = this.state[section];
        if (taxCode) {
          const taxIdType = taxIdTypes.find(txId => txId.taxCode === taxCode);
          if (taxIdType) {
            ({ placeholder } = taxIdType);
          }
        }
      }
    }
    return (
      <FormInput
        disabled={disabled}
        id={field}
        name={field}
        value={value}
        onChange={changeHandler}
        label={t(`subscriptionCreate_billing_${field}`)}
        labelClassName="col-2"
        containerClassName="col-7"
        type={type}
        groupProps={{ className: 'row' }}
        inputProps={{ ...inputProps }}
        placeholder={placeholder}
      />
    );
  }

  render() {
    const {
      t,
      countries,
      isResellerInstance,
      loggedUser,
      nextStep,
      previousStep,
    } = this.props;
    const { address } = this.state;
    const disabled = isResellerInstance && !isHigherAdmin(loggedUser);

    return (
      <div className="billing-container">
        <Form
          id="billingForm"
          className="m-form m-form--fit m-form--label-align-right"
          validationRules={this.validationRules}
          submitMethod={this.handleNavigation(true)}
          onInvalidForm={this.handleNavigation(false)}
        >
          {submitHandler => (
            <>
              {this.createInput('main', 'firstName', disabled)}
              {this.createInput('main', 'lastName', disabled)}
              {this.createInput('main', 'companyName', disabled)}
              {this.createInput('main', 'phone', disabled)}
              {this.createInput('main', 'email', disabled, 'email')}
              {this.createInput('main', 'billingLanguage', disabled, 'select')}
              <Separator />
              {this.createInput('tax_info', 'businessType', disabled, 'select')}
              {this.createInput('tax_info', 'taxCode', disabled, 'select')}
              {this.createInput('tax_info', 'taxId', disabled)}
              <Separator />
              <FormAddressFragment
                disabled={disabled}
                style={{ paddingBottom: '30px' }}
                address={address}
                onAddressChange={this.handleAddressChange}
                onFieldChange={this.handleSubSectionChange('address')}
                countries={countries}
              />
              <button
                ref={c => {
                  this.submitButton = c;
                }}
                type="submit"
                onClick={submitHandler}
                style={{ display: 'none' }}
              />
            </>
          )}
        </Form>
        <div className="wizard-content-footer">
          <button
            onClick={() => this.validateAndLeave(previousStep)}
            type="submit"
            className={
              'btn m-btn--pill m-btn--air' +
              ' btn-outline-primary m-btn m-btn--custom wizard-footer-btn'
            }
          >
            {`< ${t('previous')}`}
          </button>
          <button
            onClick={() => this.validateAndLeave(nextStep)}
            type="submit"
            className={
              'btn m-btn--pill m-btn--air btn-primary' +
              ' m-btn m-btn--custom wizard-footer-btn'
            }
          >
            {`${t('next')} >`}
          </button>
        </div>
      </div>
    );
  }
}

Billing.propTypes = {
  t: PropTypes.func.isRequired,
  nextStep: PropTypes.number.isRequired,
  previousStep: PropTypes.number.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  onNavigate: PropTypes.func.isRequired,
  showValidationErrors: PropTypes.bool,
  resetShowErrors: PropTypes.func.isRequired,
  onCountryChange: PropTypes.func.isRequired,
  isResellerInstance: PropTypes.bool.isRequired,
  loggedUser: PropTypes.shape(UserProp.propType),
  fieldsRequired: PropTypes.bool.isRequired,
  taxIdTypes: PropTypes.arrayOf(PropTypes.shape(TaxIdTypeProp.propType))
    .isRequired,
  billing: PropTypes.shape(BillingProp.propType),
  businessTypes: PropTypes.arrayOf(PropTypes.shape(BusinessTypeProp.propType))
    .isRequired,
  languages: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
};

Billing.defaultProps = {
  showValidationErrors: false,
  loggedUser: null,
  billing: BillingProp.defaultProp,
};

export default compose(translate, withLoggedUser)(Billing);
